import React, { useEffect, useState } from 'react';
import { ChevronDown, Menu, X } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
type NavSubItem = {
  label: string;
  href: string;
};
type MegaGroup = {
  heading: NavSubItem;
  items: NavSubItem[];
};
type NavItem = {
  label: string;
  href: string;
  dropdown?: NavSubItem[];
  mega?: MegaGroup[];
  highlight?: boolean;
};
// ---- Residential mega-menu (3 grouped columns) ----
const RESIDENTIAL_MEGA: MegaGroup[] = [{
  heading: {
    label: 'Mailboxes',
    href: '/6m8/mailboxes.html'
  },
  items: [{
    label: 'Complete Mailbox & Post System',
    href: '/6m8/mailboxes/complete-mailbox-post-system.html'
  }, {
    label: 'Door Chimes & Doorbells',
    href: '/6m8/mailboxes/door-chimes-doorbells.html'
  }, {
    label: 'Locking Mailboxes',
    href: '/6m8/mailboxes/locking-mailboxes.html'
  }, {
    label: 'Mailbox Replacement Parts',
    href: '/6m8/mailboxes/mailbox-replacement-parts.html'
  }, {
    label: 'Replacement Numbers',
    href: '/6m8/mailboxes/replacement-numbers.html'
  }, {
    label: 'Stone & Brick Column Mailboxes',
    href: '/6m8/mailboxes/stone-brick-column-mailboxes.html'
  }, {
    label: 'Wall Mount Mailboxes',
    href: '/6m8/mailboxes/wall-mount-mailboxes.html'
  }]
}, {
  heading: {
    label: 'Mailbox Posts',
    href: '/6m8/mailbox-posts.html'
  },
  items: [{
    label: 'Cedar Mailbox Posts',
    href: '/6m8/mailbox-posts/cedar-mailbox-posts.html'
  }, {
    label: 'Gaines Mailbox Post',
    href: '/6m8/mailbox-posts/gaines-mailbox-post.html'
  }, {
    label: 'Handy Post',
    href: '/6m8/mailbox-posts/handy-post.html'
  }, {
    label: 'Mailboss Post',
    href: '/6m8/mailbox-posts/mailboss-post.html'
  }, {
    label: 'Mayne Posts',
    href: '/6m8/mailbox-posts/mayne-posts.html'
  }, {
    label: 'Royal Crowne Mailbox Posts',
    href: '/6m8/mailbox-posts/royal-crowne-mailbox-posts.html'
  }, {
    label: 'Special Lite Products Mailbox Post',
    href: '/6m8/mailbox-posts/special-lite-products-mailbox-post.html'
  }, {
    label: 'Whitehall Mailbox Posts',
    href: '/6m8/mailbox-posts/whitehall-mailbox-posts.html'
  }]
}, {
  heading: {
    label: 'Home & Garden Accents',
    href: '/6m8/home-garden-accents.html'
  },
  items: [{
    label: 'Door Mats',
    href: '/6m8/home-garden-accents/door-mats.html'
  }, {
    label: 'Fairfield 50QT Coolers',
    href: '/6m8/home-garden-accents/fairfield-50qt-coolers.html'
  }, {
    label: 'Home Accents',
    href: '/6m8/home-garden-accents/home-accents.html'
  }, {
    label: 'Planters',
    href: '/6m8/home-garden-accents/planters.html'
  }, {
    label: 'Window Box Planters',
    href: '/6m8/home-garden-accents/window-box-planters.html'
  }]
}];
const COMMERCIAL_ITEMS: NavSubItem[] = [{
  label: 'Commercial Mailboxes & Lockers',
  href: '/6m8/commercial-mailboxes-lockers.html'
}, {
  label: 'Secure Electronic Retail Lockers',
  href: '/6m8/commercial-mailboxes-lockers/secure-electronic-retail-lockers.html'
}, {
  label: 'Secure Electronic Residential Lockers',
  href: '/6m8/commercial-mailboxes-lockers/secure-electronic-residential-lockers.html'
}, {
  label: 'Centralized Delivery Mailboxes & Kiosks',
  href: '/6m8/commercial-mailboxes-lockers/centralized-delivery-mailboxes-and-kiosks.html'
}, {
  label: 'Private Delivery Mailboxes',
  href: '/6m8/commercial-mailboxes-lockers/private-delivery-mailboxes.html'
}, {
  label: 'Parcel Lockers',
  href: '/6m8/commercial-mailboxes-lockers/parcel-lockers.html'
}, {
  label: 'Mail Slots, Letter & Book Drops',
  href: '/6m8/commercial-mailboxes-lockers/mail-slots-letter-book-drops.html'
}, {
  label: 'Collection Boxes',
  href: '/6m8/commercial-mailboxes-lockers/collection-boxes.html'
}, {
  label: 'Key Keepers & Key Blanks',
  href: '/6m8/commercial-mailboxes-lockers/key-keepers-key-blanks.html'
}, {
  label: 'Replacement Commercial Locks, Hinges & Doors',
  href: '/6m8/commercial-mailboxes-lockers/replacement-commercial-locks-hinges-doors.html'
}];
const STREET_ITEMS: NavSubItem[] = [{
  label: 'Sign Frames & Back Plates',
  href: '/6m8/street-signs-traffic-signs/sign-frames-back-plates.html'
}, {
  label: 'Traffic & Road Signs',
  href: '/6m8/street-signs-traffic-signs/traffic-road-signs.html'
}];
const DECORATIVE_ITEMS: NavSubItem[] = [{
  label: 'Decorative Stop Signs',
  href: '/6m8/decorative-signage/decorative-stop-signs.html'
}, {
  label: 'Decorative Street Sign',
  href: '/6m8/decorative-signage/decorative-street-sign.html'
}, {
  label: 'Decorative Traffic Signage',
  href: '/6m8/decorative-signage/decorative-traffic-signage.html'
}, {
  label: 'Replacement Bases, Finials & Sign Mounts',
  href: '/6m8/decorative-signage/replacement-bases-finials-sign-mounts.html'
}, {
  label: 'Sign Frames & Back Plates',
  href: '/6m8/decorative-signage/sign-frames-back-plates.html'
}];
const LIGHTING_ITEMS: NavSubItem[] = [{
  label: 'Commercial Lighting',
  href: '/6m8/lighting/commercial-lighting.html'
}, {
  label: 'Exterior Home Lighting',
  href: '/6m8/lighting/exterior-home-lighting.html'
}, {
  label: 'Light Blocks',
  href: '/6m8/lighting/light-blocks.html'
}, {
  label: 'Outdoor Exterior Lighting',
  href: '/6m8/lighting/outdoor-exterior-lighting.html'
}, {
  label: 'Outdoor Lamp Posts',
  href: '/6m8/lighting/outdoor-lamp-posts.html'
}];
const SERVICES_ITEMS: NavSubItem[] = [{
  label: 'Services',
  href: '/Services.html'
}, {
  label: 'Install Instructions',
  href: '/13.html'
}, {
  label: 'USPS Resources',
  href: '/USPSResources.html'
}, {
  label: 'Catalog',
  href: '/29.html'
}, {
  label: 'Product Option Patterns',
  href: '/product-option-patterns.html'
}, {
  label: 'Our Policies',
  href: '/OurPolicies'
}, {
  label: 'Dominion Valley Community',
  href: '/3948.html'
}];
const ABOUT_ITEMS: NavSubItem[] = [{
  label: 'About Us',
  href: '/AboutMidAtlanticMailboxInc.html'
}, {
  label: 'Our Gallery',
  href: '/3949.html'
}];
const NAV_ITEMS: NavItem[] = [{
  label: 'Residential',
  href: '/6m8/mailboxes.html',
  mega: RESIDENTIAL_MEGA
}, {
  label: 'Commercial',
  href: '/6m8/commercial-mailboxes-lockers.html',
  dropdown: COMMERCIAL_ITEMS
}, {
  label: 'Street Signs & Traffic Signs',
  href: '/6m8/street-signs-traffic-signs.html',
  dropdown: STREET_ITEMS
}, {
  label: 'Decorative Signage',
  href: '/6m8/decorative-signage.html',
  dropdown: DECORATIVE_ITEMS
}, {
  label: 'Lighting',
  href: '/6m8/lighting.html',
  dropdown: LIGHTING_ITEMS
}, {
  label: 'Services & Resources',
  href: '/Services.html',
  dropdown: SERVICES_ITEMS
}, {
  label: 'About',
  href: '/AboutMidAtlanticMailboxInc.html',
  dropdown: ABOUT_ITEMS
}, {
  label: 'Help Choosing',
  href: '/ContactUs.html',
  highlight: true
}];
// ---- Mobile drawer structure ----
type MobileGroup = {
  label: string;
  href?: string;
  // flat list of links (for simple categories)
  items?: NavSubItem[];
  // nested sub-groups (for Residential)
  subgroups?: MegaGroup[];
};
const MOBILE_GROUPS: MobileGroup[] = [{
  label: 'Residential',
  href: '/6m8/mailboxes.html',
  subgroups: RESIDENTIAL_MEGA
}, {
  label: 'Commercial',
  href: '/6m8/commercial-mailboxes-lockers.html',
  items: COMMERCIAL_ITEMS
}, {
  label: 'Street Signs & Traffic Signs',
  href: '/6m8/street-signs-traffic-signs.html',
  items: STREET_ITEMS
}, {
  label: 'Decorative Signage',
  href: '/6m8/decorative-signage.html',
  items: DECORATIVE_ITEMS
}, {
  label: 'Lighting',
  href: '/6m8/lighting.html',
  items: LIGHTING_ITEMS
}, {
  label: 'Services & Resources',
  href: '/Services.html',
  items: SERVICES_ITEMS
}, {
  label: 'About',
  href: '/AboutMidAtlanticMailboxInc.html',
  items: ABOUT_ITEMS
}, {
  label: 'Help Choosing',
  items: [{
    label: 'Contact / FAQs',
    href: '/ContactUs.html'
  }]
}];
export function Navigation() {
  const [activeMenu, setActiveMenu] = useState<string | null>(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [openGroup, setOpenGroup] = useState<string | null>('Residential');
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setMobileMenuOpen(false);
        setActiveMenu(null);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);
  const handleBlur = (e: React.FocusEvent) => {
    if (!e.currentTarget.contains(e.relatedTarget as Node)) {
      setActiveMenu(null);
    }
  };
  return <>
      {/* Mobile menu trigger bar */}
      <div className="lg:hidden bg-brand-charcoal text-white px-4 py-3 flex justify-between items-center relative z-40" data-id="element-600">
        <span className="font-serif font-bold" data-id="element-601">Shop Categories</span>
        <button type="button" onClick={() => setMobileMenuOpen(!mobileMenuOpen)} className="p-1" aria-label={mobileMenuOpen ? 'Close menu' : 'Open menu'} aria-expanded={mobileMenuOpen} data-id="element-602">
          {mobileMenuOpen ? <X size={24} data-id="element-603" /> : <Menu size={24} data-id="element-604" />}
        </button>
      </div>

      {/* Mobile Drawer (grouped, expandable) */}
      <AnimatePresence data-id="element-605">
        {mobileMenuOpen && <motion.div initial={{
        height: 0,
        opacity: 0
      }} animate={{
        height: 'auto',
        opacity: 1
      }} exit={{
        height: 0,
        opacity: 0
      }} className="lg:hidden bg-brand-charcoal text-white overflow-hidden absolute w-full z-30 shadow-xl" data-id="element-606">
            <div className="max-h-[75vh] overflow-y-auto py-2" data-id="element-607">
              {MOBILE_GROUPS.map(group => {
            const isOpen = openGroup === group.label;
            return <div key={group.label} className="border-b border-white/10 last:border-0" data-id="element-608">
                    <button type="button" onClick={() => setOpenGroup(isOpen ? null : group.label)} className="w-full flex items-center justify-between px-5 py-4 text-base font-semibold focus:outline-none focus:bg-white/10" aria-expanded={isOpen} data-id="element-609">
                      {group.label}
                      <ChevronDown size={18} className={`transition-transform ${isOpen ? 'rotate-180' : ''}`} data-id="element-610" />
                    </button>
                    <AnimatePresence data-id="element-611">
                      {isOpen && <motion.div initial={{
                  height: 0,
                  opacity: 0
                }} animate={{
                  height: 'auto',
                  opacity: 1
                }} exit={{
                  height: 0,
                  opacity: 0
                }} className="overflow-hidden bg-white/5" data-id="element-612">
                          {group.subgroups ? <div className="pb-2" data-id="element-613">
                              {group.subgroups.map(sg => <div key={sg.heading.label} className="pt-1" data-id="element-614">
                                  <a href={sg.heading.href} className="block pl-8 pr-5 pt-3 pb-1 text-sm font-semibold text-brand-gold hover:text-white focus:outline-none focus:text-white" data-id="element-615">
                                    {sg.heading.label}
                                  </a>
                                  <ul data-id="element-616">
                                    {sg.items.map(item => <li key={item.label} data-id="element-617">
                                        <a href={item.href} className="block pl-11 pr-5 py-2.5 text-sm text-[#E2DFDD] hover:text-brand-gold hover:bg-white/5 focus:outline-none focus:text-brand-gold" data-id="element-618">
                                          {item.label}
                                        </a>
                                      </li>)}
                                  </ul>
                                </div>)}
                            </div> : <ul data-id="element-619">
                              {group.href && <li data-id="element-620">
                                  <a href={group.href} className="block pl-8 pr-5 py-3 text-sm font-semibold text-brand-gold hover:text-white focus:outline-none focus:text-white" data-id="element-621">
                                    All {group.label}
                                  </a>
                                </li>}
                              {group.items?.map(item => <li key={item.label} data-id="element-622">
                                  <a href={item.href} className="block pl-8 pr-5 py-3.5 text-sm text-[#E2DFDD] hover:text-brand-gold hover:bg-white/5 focus:outline-none focus:text-brand-gold" data-id="element-623">
                                    {item.label}
                                  </a>
                                </li>)}
                            </ul>}
                        </motion.div>}
                    </AnimatePresence>
                  </div>;
          })}
            </div>
          </motion.div>}
      </AnimatePresence>

      {/* Desktop Nav */}
      <nav className="hidden lg:block bg-brand-charcoal text-white relative z-30" data-id="element-624">
        <div className="max-w-7xl mx-auto px-4" data-id="element-625">
          <ul className="flex items-center flex-nowrap w-full justify-between gap-x-1 xl:gap-x-2 text-[13px] xl:text-sm" data-id="element-626">
            {NAV_ITEMS.map(item => {
            const isRightSide = item.label === 'About' || item.label === 'Help Choosing';
            const hasMenu = Boolean(item.dropdown || item.mega);
            return <li key={item.label} className="relative" onMouseEnter={() => setActiveMenu(item.label)} onMouseLeave={() => setActiveMenu(null)} onFocus={() => setActiveMenu(item.label)} onBlur={handleBlur} data-id="element-627">
                  <a href={item.href} className={`flex items-center gap-0.5 px-2.5 xl:px-3 py-3.5 font-medium transition-colors focus:outline-none focus:bg-white/20 whitespace-nowrap ${item.highlight ? 'bg-brand-maroon hover:bg-brand-maroonDark rounded-sm' : 'hover:bg-white/10'}`} data-id="element-628">
                    {item.label}
                    {hasMenu && <ChevronDown size={14} className={`shrink-0 transition-transform ${activeMenu === item.label ? 'rotate-180' : ''}`} data-id="element-629" />}
                  </a>

                  {/* Standard dropdown */}
                  {item.dropdown && <AnimatePresence data-id="element-630">
                      {activeMenu === item.label && <motion.div initial={{
                  opacity: 0,
                  y: 10
                }} animate={{
                  opacity: 1,
                  y: 0
                }} exit={{
                  opacity: 0,
                  y: 10
                }} transition={{
                  duration: 0.15
                }} className={`absolute top-full w-80 bg-white text-brand-charcoal shadow-xl border border-brand-border rounded-b-md overflow-hidden z-40 ${isRightSide ? 'right-0' : 'left-0'}`} data-id="element-631">
                          <ul className="py-2" data-id="element-632">
                            {item.dropdown.map(subItem => <li key={subItem.label} data-id="element-633">
                                <a href={subItem.href} className="block px-5 py-2.5 text-sm hover:bg-brand-panel hover:text-brand-maroon transition-colors focus:outline-none focus:bg-brand-panel focus:text-brand-maroon" data-id="element-634">
                                  {subItem.label}
                                </a>
                              </li>)}
                          </ul>
                        </motion.div>}
                    </AnimatePresence>}

                  {/* Mega-menu (Residential) */}
                  {item.mega && <AnimatePresence data-id="element-635">
                      {activeMenu === item.label && <motion.div initial={{
                  opacity: 0,
                  y: 10
                }} animate={{
                  opacity: 1,
                  y: 0
                }} exit={{
                  opacity: 0,
                  y: 10
                }} transition={{
                  duration: 0.15
                }} className="absolute top-full left-0 w-[720px] max-w-[92vw] bg-white text-brand-charcoal shadow-xl border border-brand-border rounded-b-md overflow-hidden z-40" data-id="element-636">
                          <div className="grid grid-cols-3 divide-x divide-brand-border/60" data-id="element-637">
                            {item.mega.map(group => <div key={group.heading.label} className="p-5" data-id="element-638">
                                <a href={group.heading.href} className="block text-sm font-serif font-bold text-brand-charcoal hover:text-brand-maroon transition-colors mb-3 pb-2 border-b border-brand-border/60 focus:outline-none focus:text-brand-maroon" data-id="element-639">
                                  {group.heading.label}
                                </a>
                                <ul className="space-y-0.5" data-id="element-640">
                                  {group.items.map(subItem => <li key={subItem.label} data-id="element-641">
                                      <a href={subItem.href} className="block py-1.5 text-sm text-brand-muted hover:text-brand-maroon transition-colors focus:outline-none focus:text-brand-maroon" data-id="element-642">
                                        {subItem.label}
                                      </a>
                                    </li>)}
                                </ul>
                              </div>)}
                          </div>
                        </motion.div>}
                    </AnimatePresence>}
                </li>;
          })}
          </ul>
        </div>
      </nav>
    </>;
}